import { Button } from "@web/components/ui/button";
import { useLanguage } from "@web/lib/contexts/language-context";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2, X } from "lucide-react";

type PhotoReviewViewProps = {
	imageUrl: string;
	isValidating: boolean;
	onRetake: () => void;
	onConfirm: () => void;
};

export const PhotoReviewView = ({
	imageUrl,
	isValidating,
	onRetake,
	onConfirm,
}: PhotoReviewViewProps) => {
	const { t } = useLanguage();

	return (
		<>
			<div className="flex max-h-screen flex-1 items-center justify-center bg-black">
				<motion.img
					src={imageUrl}
					alt={t.camera.capturedPhoto}
					className="h-full w-full object-contain"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
				/>
			</div>

			<div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex h-24 items-end justify-center bg-linear-to-b from-black/70 to-transparent pb-4">
				<p className="text-sm font-medium text-white/90">
					{t.camera.capturedPhoto}
				</p>
			</div>

			<AnimatePresence>
				<motion.div
					initial={{ scale: 0.95, opacity: 0, y: 20 }}
					animate={{ scale: 1, opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					className="absolute inset-x-0 bottom-0 z-10 flex flex-row justify-center gap-4 bg-linear-to-t from-black/90 via-black/60 to-transparent px-6 pt-20 pb-8"
				>
					<div className="flex w-full max-w-sm flex-row items-center justify-center gap-4">
						<motion.div className="flex-1" whileTap={{ scale: 0.95 }}>
							<Button
								variant="secondary"
								size="lg"
								onClick={onRetake}
								disabled={isValidating}
								className="w-full rounded-full border border-white/20 bg-white/10 text-white shadow-lg backdrop-blur-md hover:bg-white/20"
							>
								<X className="mr-2 h-5 w-5" />
								{t.camera.retake}
							</Button>
						</motion.div>

						<motion.div className="flex-1" whileTap={{ scale: 0.95 }}>
							<Button
								size="lg"
								onClick={onConfirm}
								className="w-full rounded-full bg-primary text-white shadow-lg hover:bg-primary/90"
								disabled={isValidating}
							>
								{isValidating ? (
									<>
										<Loader2 className="mr-2 h-5 w-5 animate-spin" />
										{t.common.validating}
									</>
								) : (
									<>
										<Check className="mr-2 h-5 w-5" />
										{t.common.confirm}
									</>
								)}
							</Button>
						</motion.div>
					</div>
				</motion.div>
			</AnimatePresence>
		</>
	);
};
